/**
 * WebSocket 재연결 관리자
 * 재연결 시 JWT 토큰 발급 및 연결 관리
 * - Exponential backoff (횟수 제한 없음, max delay 캡)
 * - Visibility / online 시 강제 풀싱크 재연결 (좀비 소켓·데이터 재조회 포함)
 */
import { LocalStorageService, LocalStorageKey } from '../localStorage';
/** 기본 백오프 설정 */
const DEFAULT_BACKOFF = {
    initialDelay: 1000,
    maxDelay: 60000,
    backoffMultiplier: 2,
    useJitter: true,
};
/** 풀싱크 재연결 연속 호출 방지 (ms) — visibility / online 공용 */
const FULL_SYNC_RECONNECT_DEBOUNCE_MS = 1000;
/**
 * 재연결 관리자 클래스
 */
export class ReconnectManager {
    constructor(webSocketService, authService, accountService, getAccountStore, config) {
        Object.defineProperty(this, "isReconnecting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "retryTimerId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "currentDelay", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "webSocketService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "authService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "accountService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "getAccountStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "backoff", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "visibilityListenerAttached", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "onlineListenerAttached", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "fullSyncReconnectTimerId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        this.webSocketService = webSocketService;
        this.authService = authService;
        this.accountService = accountService;
        this.getAccountStore = getAccountStore;
        this.backoff = config?.backoff ?? DEFAULT_BACKOFF;
        this.currentDelay = this.backoff.initialDelay;
        this.config = {
            isEnabled: config?.isEnabled ?? (() => true),
            backoff: this.backoff,
            onReconnected: config?.onReconnected,
        };
        this.setupVisibilityReconnect();
        this.setupOnlineReconnect();
    }
    /**
     * Visibility API 기반 풀싱크 재연결
     * 탭/앱 복귀 시 소켓이 connected로 남아 있어도(좀비 포함) 강제 끊고
     * 계좌·주문·미체결 등 accountGradeChanged 경로로 재조회한다.
     */
    setupVisibilityReconnect() {
        if (this.visibilityListenerAttached || typeof document === 'undefined') {
            return;
        }
        this.visibilityListenerAttached = true;
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.scheduleFullSyncReconnect();
            }
        });
    }
    /**
     * 네트워크 복구(online) 시 풀싱크 재연결
     * Wi-Fi↔LTE 등에서 소켓이 좀비로 남는 경우에도 강제 끊고 복구한다.
     */
    setupOnlineReconnect() {
        if (this.onlineListenerAttached || typeof window === 'undefined') {
            return;
        }
        this.onlineListenerAttached = true;
        window.addEventListener('online', () => {
            this.scheduleFullSyncReconnect();
        });
    }
    /**
     * visibility / online 등에서 reconnectWithFullSync를 debounce 후 호출
     */
    scheduleFullSyncReconnect() {
        if (!this.config.isEnabled()) {
            return;
        }
        if (this.fullSyncReconnectTimerId !== null) {
            clearTimeout(this.fullSyncReconnectTimerId);
        }
        this.fullSyncReconnectTimerId = setTimeout(() => {
            this.fullSyncReconnectTimerId = null;
            if (!this.config.isEnabled()) {
                return;
            }
            this.resetBackoff();
            this.clearRetryTimer();
            void this.webSocketService.reconnectWithFullSync().catch((error) => {
                console.error('[ReconnectManager] 풀싱크 재연결 실패:', error);
            });
        }, FULL_SYNC_RECONNECT_DEBOUNCE_MS);
    }
    /**
     * 백오프 delay를 초기값으로 리셋
     */
    resetBackoff() {
        this.currentDelay = this.backoff.initialDelay;
    }
    /**
     * 다음 재시도 예약 (exponential backoff, max delay 캡)
     * 호출 시점: 재연결 실패 후. isEnabled만 확인 (의도적 disconnect 시 예약하지 않음)
     * useJitter 사용 시 Equal jitter 적용 (thundering herd 방지)
     */
    scheduleNextRetry() {
        if (!this.config.isEnabled()) {
            return;
        }
        this.clearRetryTimer();
        const baseDelay = this.currentDelay;
        const delay = this.backoff.useJitter !== false
            ? baseDelay / 2 + (baseDelay / 2) * Math.random()
            : baseDelay;
        const delayMs = Math.round(delay);
        console.warn(`[ReconnectManager] ${delayMs}ms 후 재연결 시도 예정`);
        this.retryTimerId = setTimeout(() => {
            this.retryTimerId = null;
            void this.attemptReconnect();
        }, delayMs);
        this.currentDelay = Math.min(this.currentDelay * this.backoff.backoffMultiplier, this.backoff.maxDelay);
    }
    /**
     * 예약된 재연결 타이머 해제
     */
    clearRetryTimer() {
        if (this.retryTimerId !== null) {
            clearTimeout(this.retryTimerId);
            this.retryTimerId = null;
        }
    }
    /**
     * 재연결이 가능한 상태인지 확인
     */
    canReconnect() {
        // 이미 연결됨이면 재연결 불필요
        if (this.webSocketService.isConnected()) {
            return false;
        }
        // connect() 진행 중이면 중복 재연결 스케줄 방지 (재연결 진행 여부는 isReconnecting으로 판단)
        if (this.webSocketService.getConnectionState() === 'connecting') {
            return false;
        }
        if (!this.config.isEnabled()) {
            return false;
        }
        if (this.isReconnecting) {
            return false;
        }
        return true;
    }
    /**
     * 재연결 스케줄 (initialDelay 후 첫 시도)
     * onclose 시 호출. 즉시 연결 시도하지 않고 delay 후 attemptReconnect 호출
     */
    scheduleReconnect() {
        if (!this.canReconnect()) {
            return;
        }
        this.scheduleNextRetry();
    }
    /**
     * 자동 재연결 시도
     * 실패 시 exponential backoff로 다음 재시도 예약 (횟수 제한 없음)
     * @param options.force - true면 이미 connected/connecting이어도 풀싱크 경로를 강제 수행
     */
    async attemptReconnect(options) {
        if (options?.force) {
            if (!this.config.isEnabled()) {
                return;
            }
            this.clearRetryTimer();
            this.isReconnecting = true;
            try {
                await this.refreshTokenAndReconnect();
            }
            catch (error) {
                console.error('[ReconnectManager] 강제 풀싱크 재연결 실패:', error);
                this.scheduleNextRetry();
            }
            finally {
                this.isReconnecting = false;
            }
            return;
        }
        if (!this.canReconnect()) {
            return;
        }
        this.isReconnecting = true;
        try {
            await this.refreshTokenAndReconnect();
        }
        catch (error) {
            console.error('[ReconnectManager] 재연결 실패:', error);
            this.scheduleNextRetry();
        }
        finally {
            this.isReconnecting = false;
        }
    }
    /**
     * 재연결 취소 (타이머 해제, 백오프 리셋)
     */
    cancelReconnect() {
        this.isReconnecting = false;
        this.clearRetryTimer();
        if (this.fullSyncReconnectTimerId !== null) {
            clearTimeout(this.fullSyncReconnectTimerId);
            this.fullSyncReconnectTimerId = null;
        }
        this.resetBackoff();
    }
    /**
     * 자동 재연결 활성화/비활성화 설정
     * @param enabled - true면 재연결 활성화, false면 비활성화
     */
    setAutoReconnect(enabled) {
        this.config.isEnabled = () => enabled;
    }
    /**
     * 토큰 갱신 후 재연결
     * - 로그인 + 계좌 선택됨: JWT 발급 후 재연결
     * - 비로그인 또는 계좌 미선택: 토큰 없이 재연결
     */
    async refreshTokenAndReconnect() {
        const accessToken = LocalStorageService.getItem(LocalStorageKey.ACCESS_TOKEN);
        const accountStore = this.getAccountStore();
        const selectedAccountNo = accountStore.selectedAccountNo;
        if (accessToken && selectedAccountNo) {
            await this.reconnectWithToken(selectedAccountNo, accountStore);
        }
        else {
            await this.reconnectWithoutToken();
        }
    }
    /**
     * 토큰을 발급받아 재연결 (로그인 상태)
     * 재연결 시 계좌 등급 변경 가능성을 고려하여 accountList도 갱신합니다.
     *
     * 재연결 시에는 계좌 등급 변경 여부와 관계없이
     * handleAccountGradeChanged를 호출하여 관련 데이터 재로드
     */
    async reconnectWithToken(accountNo, accountStore) {
        // 계좌 목록 갱신 - 계좌 등급 변경 사항 반영
        try {
            const accountInfoResponse = await this.accountService.getAccountInfo();
            if (accountInfoResponse.data?.accountList) {
                await accountStore.setAccountList(accountInfoResponse.data.accountList);
            }
        }
        catch (error) {
            console.error('[ReconnectManager] 계좌 목록 갱신 실패:', error);
            // 계좌 목록 갱신 실패해도 재연결은 계속 진행
        }
        // 재연결 시 풀싱크: 계좌 등급 변경 여부와 관계없이 무조건 호출
        const currentAccount = accountStore.accountList.find((acc) => acc.accountNo === accountNo);
        const currentGrade = currentAccount?.accountGrade ?? '';
        if (accountStore.handleAccountGradeChanged) {
            await accountStore.handleAccountGradeChanged({
                previousGrade: currentGrade,
                currentGrade,
                accountNo,
                previousAccountNo: accountNo,
            });
        }
        // Account Token 발급
        const response = await this.authService.getAccountJwtToken({ accountNo });
        // 토큰 발급 실패 시 에러 throw
        if (!response.data?.token) {
            throw new Error('WebSocket JWT 토큰 발급 실패');
        }
        // 새 토큰 설정
        const newWsToken = response.data.token;
        this.webSocketService.setAccessToken(newWsToken);
        // AccountStore에도 토큰 업데이트 (일관성 유지)
        accountStore.setAccountToken(newWsToken);
        // WebSocket 연결 시도
        await this.webSocketService.connect();
        if (this.config.onReconnected) {
            await this.config.onReconnected();
        }
    }
    /**
     * 토큰 없이 재연결 (비로그인 상태)
     */
    async reconnectWithoutToken() {
        this.webSocketService.clearAccessToken();
        // WebSocket 연결 시도
        await this.webSocketService.connect();
        if (this.config.onReconnected) {
            await this.config.onReconnected();
        }
    }
}
//# sourceMappingURL=ReconnectManager.js.map